'use client';

import { useState, useEffect, useMemo } from 'react';
import { siteConfig } from '@/lib/siteConfig';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
  ArrowUpRight, ArrowRight, Menu, X, MonitorCheck, ChevronDown, PhoneCall, MessageSquare, CodeXml, PencilRuler, Cpu, DatabaseSearch, ShoppingCart, TabletSmartphone,
  Smartphone, ShieldCheck, Bug, Bot, BriefcaseMedical, Building2, GraduationCap, ShoppingBag, Landmark, Plane, Factory, Store,
  UtensilsCrossed, Hotel, Brain
} from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { useLivePaths } from '@/context/LiveNavContext';
import { filterLiveLinks, filterLiveGroups } from '@/lib/live-nav';
import { STORE_VISIBLE } from '@/lib/storeConfig';

const SERVICES_TABS = [
  {
    id: 'software', label: 'Software Development',
    links: [
      { label: 'Custom Software Development', href: '/custom-software-development' },
      { label: 'MVP Development Services', href: '/mvp-development-services' },
      { label: 'Enterprise Software Development', href: '/enterprise-software-development' },
      { label: 'Legacy System Modernization', href: '/legacy-software-modernization-services' },
      { label: 'SaaS Development Services', href: '/saas-development-services' },
      { label: 'Software Support & Maintenance', href: '/software-maintenance-services' },
    ],
    icon: <CodeXml size={16} />,
    heading: 'Software Development Services',
    desc: 'We design and build scalable web applications, custom software solutions, and digital products that help businesses automate workflows, reduce operational costs, and accelerate time-to-market.',
    bullets: ['Ideate', 'Engineer', 'Launch'],
    exploreHref: '/software-development-services',
    image: '/images/services/header/software.webp',
  },
  {
    id: 'ai', label: 'AI Development Services',
    links: [
      { label: 'AI Chatbot Services', href: '/ai-chatbot-development-services' },
      { label: 'AI App Development', href: '/ai-app-development-services' },
      { label: 'AI Integration Services', href: '/ai-integration-services' },
      { label: 'Enterprise AI Development', href: '/enterprise-ai-development' },
      { label: 'Machine Learning Development', href: '/machine-learning-development' },
      { label: 'Enterprise AI Chatbot Development', href: '/enterprise-ai-chatbot' },
      { label: 'Generative AI Development', href: '/generative-ai-development-services' },
      { label: 'Gen AI Integration Services', href: '/gen-ai-integration-services' },
      { label: 'AI Agents Development', href: '/ai-agents-development' },
      { label: 'RAG Development', href: '/rag-development' },
    ],
    icon: <Cpu size={16} />,
    heading: 'AI Development Services & Solutions',
    desc: 'Build smarter products with our end-to-end AI development services - from machine learning models and generative AI to enterprise chatbots, RAG pipelines, and intelligent automation.',
    bullets: ['Analyze', 'Train', 'Automate'],
    exploreHref: '/artificial-intelligence-services',
    image: '/images/services/header/ai.webp',
  },
  {
    id: 'mobile', label: 'Mobile App Development',
    links: [
      { label: 'iOS App Development', href: '/ios-app-development-services' },
      { label: 'Android App Development', href: '/android-app-development-services' },
      { label: 'Cross-Platform Development', href: '/cross-platform-app-development-services' },
      { label: 'Custom Mobile App Development', href: '/custom-app-development-services' },
      { label: 'Swift App Development', href: '/swift-app-development-services' },
      { label: 'Kotlin App Development', href: '/kotlin-app-development' },
      { label: 'Flutter App Development', href: '/flutter-app-development' },
      { label: 'React Native App Development', href: '/react-native-app-development' },
      { label: 'Hybrid App Development', href: '/hybrid-app-development-services' },
      { label: 'Wearable App Development', href: '/wearable-app-development' },
    ],
    icon: <Smartphone size={16} />,
    heading: 'Mobile App Development Services',
    desc: 'We develop high-performance iOS, Android, and cross-platform mobile applications tailored to your business goals - built for speed, security, and seamless user experience.',
    bullets: ['Design', 'Develop', 'Deploy'],
    exploreHref: '/mobile-app-development-services',
    image: '/images/services/header/mobile.webp',
  },
  {
    id: 'web', label: 'Web Development Services',
    links: [
      { label: 'Frontend Development', href: '/technology/frontend' },
      { label: 'Backend Development', href: '/backend-development-services' },
      { label: 'Full Stack Development', href: '/full-stack-development-services' },
      { label: 'MERN Stack Development', href: '/mern-stack-development-services' },
      { label: 'Web Application Development', href: '/web-application-development-services' },
      { label: 'Custom Website Development', href: '/services/website-development/custom-website-development' },
      { label: 'CMS Development', href: '/cms-development-services' },
      { label: 'eCommerce Website Development', href: '/ecommerce-website-development' },
      { label: 'Responsive Website Design', href: '/responsive-website-design-services' },
    ],
    icon: <MonitorCheck size={16} />,
    heading: 'Web Development Services',
    desc: 'From frontend interfaces to full-stack architectures, we deliver fast, SEO-friendly, and scalable web applications that convert visitors and grow your digital presence.',
    bullets: ['Architect', 'Build', 'Optimize'],
    exploreHref: '/web-development-services',
    image: '/images/services/header/web.webp',
  },
  {
    id: 'application', label: 'Application Services',
    links: [
      { label: 'Application Development', href: '/application-development-services' },
      { label: 'Web Application Services', href: '/web-application-development' },
      { label: 'Enterprise Application Development', href: '/enterprise-application-development' },
      { label: 'Application Modernization', href: '/application-modernization-services' },
      { label: 'Application Integration', href: '/application-integration-services' },
      { label: 'Application Maintenance and Support', href: '/application-maintenance-and-support-services' },
    ],
    icon: <TabletSmartphone size={16} />,
    heading: 'Enterprise Application Services',
    desc: 'We build, modernize, and integrate enterprise applications that scale with your business - improving agility, reducing technical debt, and connecting your critical systems.',
    bullets: ['Modernize', 'Integrate', 'Scale'],
    exploreHref: '/application-development-services',
    image: '/images/services/header/application.webp',
  },
  {
    id: 'ecommerce', label: 'eCommerce Development',
    links: [
      { label: 'Shopify Development', href: '/shopify-development' },
      { label: 'WordPress Development', href: '/custom-wordpress-development' },
      { label: 'WooCommerce Development', href: '/woocommerce-development' },
      { label: 'Magento Development', href: '/magento-development' },
      { label: 'Custom eCommerce Development', href: '/custom-ecommerce-development' },
      { label: 'Third-party Integrations', href: '/third-party-integrations' },
    ],
    icon: <ShoppingCart size={16} />,
    heading: 'eCommerce Development Services',
    desc: 'Launch and scale high-converting online stores with our eCommerce development expertise - covering Shopify, WooCommerce, Magento, and fully custom platforms built for growth.',
    bullets: ['Configure', 'Customize', 'Convert'],
    exploreHref: '/ecommerce-development-services',
    allowWithoutParent: true,
    image: '/images/services/header/ecommerce.webp',
  },
  {
    id: 'ui', label: 'UX/UI',
    links: [
      { label: 'User Interface (UI) Design', href: '/ui-design-services' },
      { label: 'User Experience (UX) Design', href: '/ux-design-services' },
      { label: 'Website Redesign', href: '/website-redesign-services' },
      { label: 'E-commerce Web Design', href: '/ecommerce-website-design-services' },
      { label: 'SaaS UI Design', href: '/saas-ui-design-services' },
    ],
    icon: <PencilRuler size={16} />,
    heading: 'UI/UX Design Services',
    desc: 'We create intuitive, conversion-driven user interfaces and experiences that reduce friction, increase engagement, and reflect your brand - across web, SaaS, and mobile platforms.',
    bullets: ['Research', 'Design', 'Validate'],
    exploreHref: '/ui-ux-design-services',
    image: '/images/services/header/ui.webp',
  },
  {
    id: 'data', label: 'Data Analytics',
    links: [
      { label: 'Business Intelligence', href: '/business-intelligence-services' },
      { label: 'Data Management', href: '/data-management-services' },
      { label: 'Data Analytics as a Service', href: '/data-analytics-as-a-service' },
    ],
    icon: <DatabaseSearch size={16} />,
    heading: 'Data Analytics & Business Intelligence Services',
    desc: 'Turn raw data into revenue-driving decisions with our data analytics services - covering business intelligence, data management, visualization, and analytics-as-a-service solutions.',
    bullets: ['Collect', 'Analyze', 'Decide'],
    exploreHref: '/data-analytics-services',
    image: '/images/services/header/data.webp',
  },
  {
    id: 'api', label: 'API and Integration Services',
    links: [
      { label: 'REST API Development ', href: '/rest-api-development-services' },
      { label: 'Third Party Integrations ', href: '/third-party-integration-services' },
      { label: 'CRM / ERP Integration', href: '/crm-erp-integration-services' },
    ],
    icon: <ShieldCheck size={16} />,
    heading: 'API Development & Integration Services',
    desc: 'Connect your tech stack with reliable REST API development, third-party integrations, and CRM/ERP connectivity - ensuring seamless data flow across your business systems.',
    bullets: ['Connect', 'Automate', 'Sync'],
    exploreHref: '/api-development-integration-services',
    image: '/images/services/header/api.webp',
  },
  {
    id: 'testing', label: 'Testing & QA',
    links: [
      { label: 'Functional Testing', href: '/functional-testing-services' },
      { label: 'Performance Testing', href: '/performance-testing-services' },
      { label: 'Security Testing', href: '/security-testing-services' },
      { label: 'Usability Testing', href: '/usability-testing-services' },
      { label: 'Automation Testing', href: '/automation-testing-services' },
      { label: 'Application Testing', href: '/application-testing-services' },
      { label: 'Vulnerability and Penetration Testing', href: '/vulnerability-and-penetration-testing-services' },
      { label: 'Compliance Testing', href: '/compliance-testing-services' },
    ],
    icon: <Bug size={16} />,
    heading: 'Software Testing & QA Services',
    desc: 'Ship with confidence using our end-to-end QA and software testing services - covering functional, performance, security, automation, and penetration testing for every release cycle.',
    bullets: ['Test', 'Debug', 'Certify'],
    exploreHref: '/software-testing-and-qa-services',
    allowWithoutParent: true,
    image: '/images/services/header/testing.webp',
  },
];

const SERVICES_DIRECT = [
  { label: 'Robotic Process Automation', href: '/robotic-process-automation-services' },
];

const RPA_TAB = {
  id: 'rpa',
  label: 'Robotic Process Automation',
  icon: <Bot size={16} />,
  links: [],
  heading: 'Robotic Process Automation',
  desc: 'Automate repetitive business processes to eliminate manual work, reduce errors, and scale operations efficiently.',
  bullets: [],
  exploreHref: '/robotic-process-automation-services',
  image: '/images/services/header/testing.webp',
};

function buildAllServiceTabs(serviceTabs) {
  return [...serviceTabs, RPA_TAB];
}

const INDUSTRIES = [
  { label: 'Healthcare', href: '/industries/healthcare', icon: <BriefcaseMedical size={16} /> },
  { label: 'Real Estate', href: '/industries/real-estate', icon: <Building2 size={16} /> },
  { label: 'AI & Automation', href: '/industries/ai-automation', icon: <Bot size={16} /> },
  { label: 'Education', href: '/industries/education', icon: <GraduationCap size={16} /> },
  { label: 'E-commerce', href: '/industries/ecommerce', icon: <ShoppingBag size={16} /> },
  { label: 'Finance', href: '/industries/finance', icon: <Landmark size={16} /> },
  { label: 'Travel & Tourism', href: '/industries/travel-tourism', icon: <Plane size={16} /> },
  { label: 'Manufacturing', href: '/industries/manufacturing', icon: <Factory size={16} /> },
  { label: 'Retail', href: '/industries/retail', icon: <Store size={16} /> },
  { label: 'Restaurant', href: '/industries/restaurant', icon: <UtensilsCrossed size={16} /> },
  { label: 'Hospitality', href: '/industries/hospitality', icon: <Hotel size={16} /> },
  { label: 'Machine Learning', href: '/industries/machine-learning', icon: <Brain size={16} /> },
];

const HIRE_DEV = [
  {
    category: 'Full Stack',
    label: 'Hire Full Stack Developer',
    href: '/hire-full-stack-developers',
    children: [
      { label: 'Hire MERN Stack Developer', href: '/hire-mern-stack-developers' },
      { label: 'Hire MEAN Stack Developer', href: '/hire-mean-stack-developers' },
      { label: 'Hire JavaScript Developer', href: '/hire-javascript-developers' },
    ],
  },
  {
    category: 'AI & ML',
    label: 'Hire AI Developer',
    href: '/hire-ai-developers',
    children: [
      { label: 'Hire ML Engineer', href: '/hire-machine-learning-engineers' },
      { label: 'Hire Generative AI Developer', href: '/hire-generative-ai-developers' },
      { label: 'Hire LLM Developer', href: '/hire-llm-developers' },
      { label: 'Hire Prompt Engineer', href: '/hire-prompt-engineers' },
      { label: 'Hire Computer Vision Engineer', href: '/hire-computer-vision-engineers' },
    ],
  },
  {
    category: 'Frontend',
    label: 'Hire Frontend Developer',
    href: '/hire-frontend-developers',
    children: [
      { label: 'Hire React.js Developer', href: '/hire-reactjs-developers' },
      { label: 'Hire Next.js Developer', href: '/hire-nextjs-developers' },
      { label: 'Hire Vue.js Developer', href: '/hire-vuejs-developers' },
      { label: 'Hire Angular Developer', href: '/hire-angular-developers' },
      { label: 'Hire TypeScript Developer', href: '/hire-typescript-developers' },
    ],
  },
  {
    category: 'Backend',
    label: 'Hire Backend Developer',
    href: '/hire-backend-developers',
    children: [
      { label: 'Hire Node.js Developer', href: '/hire-nodejs-developers' },
      { label: 'Hire Python Developer', href: '/hire-python-developers' },
      { label: 'Hire Laravel Developer', href: '/hire-laravel-developers' },
      { label: 'Hire PHP Developer', href: '/hire-php-developers' },
      { label: 'Hire Java Developer', href: '/hire-java-developers' },
      { label: 'Hire Django Developer', href: '/hire-django-developers' },
      { label: 'Hire .NET Developer', href: '/hire-dotnet-developers' },
    ],
  },
  {
    category: 'Mobile App',
    label: 'Hire Mobile App Developer',
    href: '/hire-mobile-app-developers',
    children: [
      { label: 'Hire iOS Developer', href: '/hire-ios-developers' },
      { label: 'Hire Android Developer', href: '/hire-android-developers' },
      { label: 'Hire React Native Developer', href: '/hire-react-native-developers' },
      { label: 'Hire Flutter Developer', href: '/hire-flutter-developers' },
      { label: 'Hire Swift Developer', href: '/hire-swift-developers' },
      { label: 'Hire Kotlin Developer', href: '/hire-kotlin-developers' },
    ],
  },
  {
    category: 'Cloud & DevOps',
    label: 'Hire DevOps Engineer',
    href: '/hire-devops-engineers',
    children: [
      { label: 'Hire AWS Developer', href: '/hire-aws-developers' },
      { label: 'Hire Azure Developer', href: '/hire-azure-developers' },
      { label: 'Hire Cloud Engineer', href: '/hire-cloud-engineers' },
    ],
  },
  {
    category: 'CMS & eCommerce',
    label: 'Hire CMS Developer',
    href: '/hire-cms-developers',
    children: [
      { label: 'Hire WordPress Developer', href: '/hire-wordpress-developers' },
      { label: 'Hire Shopify Developer', href: '/hire-shopify-developers' },
      { label: 'Hire Magento Developer', href: '/hire-magento-developers' },
      { label: 'Hire WooCommerce Developer', href: '/hire-woocommerce-developers' },
      { label: 'Hire Webflow Developer', href: '/hire-webflow-developers' },
    ],
  },
  {
    category: 'Design',
    label: 'Hire UI/UX Designer',
    href: '/hire-ui-ux-designers',
    children: [
      { label: 'Hire Product Designer', href: '/hire-product-designers' },
      { label: 'Hire Figma Designer', href: '/hire-figma-designers' },
      { label: 'Hire Web Designer', href: '/hire-web-designers' },
    ],
  },
  {
    category: 'QA & Testing',
    label: 'Hire QA Engineer',
    href: '/hire-qa-engineers',
    children: [
      { label: 'Hire Automation Engineer', href: '/hire-automation-test-engineers' },
      { label: 'Hire Performance Tester', href: '/hire-performance-test-engineers' },
      { label: 'Hire Security Tester', href: '/hire-security-test-engineers' },
    ],
  },
];

const TECH = [
  {
    category: 'AI & ML',
    label: 'AI & ML Technologies',
    href: '/technology#ai-ml',
    children: [
      { label: 'OpenAI', href: '/technology/openai' },
      { label: 'Anthropic', href: '/technology/anthropic' },
      { label: 'GPT', href: '/technology/gpt' },
      { label: 'Grok', href: '/technology/grok' },
      { label: 'TensorFlow', href: '/technology/tensorflow' },
      { label: 'Open CV', href: '/technology/open-cv' },
    ],
  },
  {
    category: 'Frontend',
    label: 'Frontend Technologies',
    href: '/technology/frontend',
    children: [
      { label: 'React.js', href: '/technology/reactjs' },
      { label: 'Next.js', href: '/technology/nextjs' },
      { label: 'TypeScript', href: '/technology/typescript' },
      { label: 'Tailwind CSS', href: '/technology/tailwind-css' },
      { label: 'Vue.js', href: '/technology/vuejs' },
      { label: 'Angular', href: '/technology/angular' },
    ],
  },
  {
    category: 'Backend',
    label: 'Backend Technologies',
    href: '/technology#backend',
    children: [
      { label: 'Node.js', href: '/technology/node' },
      { label: 'Python', href: '/technology/python' },
      { label: 'Go (Golang)', href: '/technology/golang' },
      { label: '.NET', href: '/technology/dotnet' },
      { label: 'Django', href: '/technology/django' },
      { label: 'PHP', href: '/technology/php' },
      { label: 'Laravel', href: '/technology/laravel' },
      { label: 'Ruby on Rails', href: '/technology/ruby-on-rails' },
    ],
  },
  {
    category: 'Cloud & DevOps',
    label: 'Cloud & DevOps',
    href: '/technology#cloud',
    children: [
      { label: 'AWS', href: '/technology/aws' },
      { label: 'Microsoft Azure', href: '/technology/azure' },
      { label: 'Google Cloud', href: '/technology/google-cloud' },
      { label: 'Kubernetes', href: '/technology/kubernetes' },
      { label: 'Docker', href: '/technology/docker' },
      { label: 'CI/CD Pipelines', href: '/technology/ci-cd' },
    ],
  },
  {
    category: 'Database',
    label: 'Database Technologies',
    href: '/technology#database',
    children: [
      { label: 'PostgreSQL', href: '/technology/postgresql' },
      { label: 'MongoDB', href: '/technology/mongodb' },
      { label: 'Redis', href: '/technology/redis' },
      { label: 'MySQL', href: '/technology/sql' },
      { label: 'Firebase', href: '/technology/firebase' },
    ],
  },
  {
    category: 'Mobile',
    label: 'Mobile Technologies',
    href: '/technology#mobile',
    children: [
      { label: 'Flutter', href: '/technology/flutter' },
      { label: 'React Native', href: '/technology/react-native' },
      { label: 'Kotlin', href: '/technology/kotlin' },
      { label: 'Swift', href: '/technology/swift' },
      { label: 'Spingboot', href: '/technology/spingboot' },
      { label: 'Ktor', href: '/technology/ktor' },
    ],
  },
  {
    category: 'QA & Testing',
    label: 'QA & Testing Tools',
    href: '/technology#testing-automation',
    children: [
      { label: 'Playwright', href: '/technology/playwright' },
      { label: 'Cypress', href: '/technology/cypress' },
      { label: 'Jest', href: '/technology/jest' },
      { label: 'Selenium', href: '/technology/selenium' },
      { label: 'Postman', href: '/technology/postman' },
    ],
  },
  {
    category: 'CMS & eCommerce',
    label: 'CMS & eCommerce',
    href: '/technology#cms-ecommerce',
    children: [
      { label: 'WordPress', href: '/technology/wordpress' },
      { label: 'Shopify', href: '/technology/shopify' },
      { label: 'Webflow', href: '/technology/webflow' },
      { label: 'WooCommerce', href: '/technology/woocommerce' },
      { label: 'Magento', href: '/technology/magento' },
    ],
  },
];

const RESOURCES = [
  { label: 'Articles', href: '/blog' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Portfolio', href: '/portfolio' },
  { label: 'Client Reviews', href: '/client-reviews' },
];

// ── Active menu detection ─────────────────────────────────────────────────────

function getActiveMenu(pathname, { allServiceTabs, industries, hireDev, tech, resources }) {
  const serviceHrefs = new Set(
    allServiceTabs.flatMap(t => [t.exploreHref, ...t.links.map(l => l.href)])
  );
  if (serviceHrefs.has(pathname)) return 'services';
  if (industries.some(i => i.href === pathname)) return 'industries';
  if (hireDev.some(i => i.href === pathname || i.children?.some(c => c.href === pathname))) return 'hire';
  if (tech.some(i => i.href === pathname || i.children?.some(c => c.href === pathname))) return 'tech';
  if (pathname === '/about-us') return 'about';
  if (pathname === '/store') return 'store';
  if (resources.some(i => i.href === pathname)) return 'resources';
  return null;
}

function getInitialServiceTab(pathname, allServiceTabs) {
  for (const tab of allServiceTabs) {
    if (tab.exploreHref === pathname) return tab.id;
    if (tab.links.some(l => l.href === pathname)) return tab.id;
  }
  return 'software';
}

// ── Services mega menu ────────────────────────────────────────────────────────

function ServicesMegaMenu({ pathname, tabs }) {
  const [active, setActive] = useState(() => getInitialServiceTab(pathname ?? '', tabs));
  const tab = tabs.find(t => t.id === active) ?? tabs[0];

  if (!tab) return null;

  return (
    <div className="w-full rounded-xl overflow-hidden bg-white flex">

      {/* Left — all parent categories, vertical list */}
      <div className="w-75 shrink-0 border-r border-slate-100 py-4 px-3 flex flex-col gap-0.5">
        {tabs.map(t => (
          <button
            key={t.id}
            onMouseEnter={() => setActive(t.id)}
            onClick={() => setActive(t.id)}
            className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-150 border ${t.id === active
              ? 'bg-secondary/8 text-secondary font-semibold border-secondary/15'
              : 'text-secondary/65 border-transparent hover:text-secondary hover:bg-secondary/5 hover:border-secondary/10 hover:font-semibold'
              }`}
          >
            <span className='flex items-center gap-2'>{t.icon} {t.label}</span>
            {t.id === active && <ArrowUpRight size={13} className="shrink-0 text-secondary/50" />}
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col">
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between gap-6">
          <div className="min-w-0">
            <Link
              className='group/heading flex items-center gap-1'
              href={tab.exploreHref}
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }))}
            >
              <p className="text-base font-bold text-slate-900 group-hover/heading:text-primary leading-snug mb-1 transition-colors duration-200">
                {tab.heading}
              </p>
              <ArrowUpRight size={14} className="text-primary opacity-0 group-hover/heading:opacity-100 group-hover/heading:translate-x-0.5 transition-all duration-200 ease-out mb-1 shrink-0" />
            </Link>
            <p className="text-sm text-slate-500">{tab.desc}</p>
          </div>
        </div>

        {tab.links.length > 0 && (
          <div className="p-4 flex-1 bg-slate-50/50">
            <div className="grid grid-cols-3 gap-2 h-full">
              <div className="col-span-2 grid grid-cols-2 gap-0.5 content-start">
                {tab.links.map(l => (
                  <NavigationMenuLink asChild key={l.href}>
                    <Link
                      href={l.href}
                      className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-transparent hover:bg-white hover:border-slate-100 transition-all"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-secondary group-hover:text-secondary group-hover:font-semibold transition-all leading-snug truncate">
                          {l.label}
                        </span>
                      </span>
                      <ArrowUpRight size={12} className="text-secondary group-hover:text-secondary transition-transform duration-200 ease-out group-hover:translate-x-1.5 shrink-0" />
                    </Link>
                  </NavigationMenuLink>
                ))}
              </div>
              <NavigationMenuLink asChild>
                <Link href={tab.exploreHref} className="relative overflow-hidden rounded-xl group/img block min-h-[140px]">
                  {tab.image ? (
                    <Image
                      src={tab.image}
                      alt={tab.heading}
                      loading="lazy"
                      fill
                      sizes="(max-width: 1400px) 20vw, 280px"
                      className="object-cover rounded-xl transition-transform duration-500 group-hover/img:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/10 via-secondary/5 to-slate-100 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-white/60 backdrop-blur-sm flex items-center justify-center text-secondary shadow-sm">
                        {tab.icon}
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/60 to-secondary/40 rounded-xl flex flex-col justify-end gap-1.5 p-4">
                    <div className='flex items-center gap-1.5'>
                      <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center text-white shrink-0 group-hover/img:text-primary">
                        {tab.icon}
                      </div>
                      <p className="text-white group-hover/img:text-primary font-bold text-sm leading-snug transition-colors duration-200">
                        Explore {tab.label}
                      </p>
                    </div>
                  </div>
                </Link>
              </NavigationMenuLink>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Industries — 3-col grid ───────────────────────────────────────────────
function IndustriesMenu({ items }) {
  return (
    <div className="p-1 w-160">
      <div className="grid grid-cols-3 gap-0.5">
        {items.map(item => (
          <NavigationMenuLink asChild key={item.href}>
            <Link
              href={item.href}
              className="group/industry flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-secondary group-hover/industry:text-secondary transition-colors">
                {item.icon} {item.label}
              </span>
              <ArrowUpRight size={13} className="text-secondary/50 opacity-0 group-hover/industry:opacity-100 group-hover/industry:text-secondary transition-all duration-200 ease-out group-hover/industry:translate-x-1.5 shrink-0" />
            </Link>
          </NavigationMenuLink>
        ))}
      </div>
    </div>
  );
}

// ── Hire Developer — 2-col grid ───────────────────────────────────────────────
function HireDevMenu({ groups }) {
  const [activeCategory, setActiveCategory] = useState(groups[0]?.category);
  const active = groups.find(d => d.category === activeCategory) ?? groups[0];

  if (!active) return null;

  return (
    <div className="flex p-1 w-[700px]">

      {/* ── Left: category tabs ───────────────────────────── */}
      <div className="w-44 shrink-0 border-r border-slate-100 pr-1 py-0.5">
        {groups.map(item => (
          <button
            key={item.category}
            onMouseEnter={() => setActiveCategory(item.category)}
            onClick={() => setActiveCategory(item.category)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors
              ${activeCategory === item.category
                ? 'bg-slate-100 text-secondary font-semibold'
                : 'text-secondary/60 hover:bg-slate-50 hover:text-secondary font-medium'
              } text-sm`}
          >
            {item.category}
            <ArrowRight size={12} className="shrink-0 opacity-40" />
          </button>
        ))}
      </div>

      {/* ── Right: parent + children ──────────────────────── */}
      <div className="flex-1 px-2 py-0.5">

        {/* Parent pillar link */}
        <NavigationMenuLink asChild>
          <Link
            href={active.href}
            className="group flex items-center justify-between gap-2 px-3 py-2.5 mb-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-100"
          >
            <span className="text-sm font-semibold text-secondary group-hover:text-secondary transition-colors">
              {active.label}
            </span>
            <ArrowUpRight size={13} className="text-secondary/50 group-hover:text-secondary transition-transform duration-200 ease-out group-hover:translate-x-1.5 group-hover:-translate-y-0.5 shrink-0" />
          </Link>
        </NavigationMenuLink>

        {/* Divider */}
        <div className="mx-1 mb-1.5 border-t border-slate-100" />

        {/* Children 2-col grid — matches your existing pattern */}
        <div className="grid grid-cols-2 gap-0.5">
          {active.children.map(child => (
            <NavigationMenuLink asChild key={child.href}>
              <Link
                href={child.href}
                className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-secondary/70 group-hover:text-secondary transition-colors">
                    {child.label}
                  </span>
                </span>
                <ArrowUpRight size={13} className="text-secondary/50 opacity-0 group-hover:opacity-100 group-hover:text-secondary transition-all duration-200 ease-out group-hover:translate-x-1.5 shrink-0" />
              </Link>
            </NavigationMenuLink>
          ))}
        </div>

      </div>
    </div>
  );
}

// ── TECH — category tabs + children grid ────────────────────────────────────
function TechMenu({ groups }) {
  const [activeCategory, setActiveCategory] = useState(groups[0]?.category);
  const active = groups.find(t => t.category === activeCategory) ?? groups[0];

  if (!active) return null;

  return (
    <div className="flex p-1 w-[600px]">

      {/* Left: category tabs */}
      <div className="w-44 shrink-0 border-r border-slate-100 pr-1 py-0.5">
        {groups.map(item => (
          <button
            key={item.category}
            onMouseEnter={() => setActiveCategory(item.category)}
            onClick={() => setActiveCategory(item.category)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left transition-colors text-sm
              ${activeCategory === item.category
                ? 'bg-slate-100 text-secondary font-semibold'
                : 'text-secondary/60 hover:bg-slate-50 hover:text-secondary font-medium'
              }`}
          >
            {item.category}
            <ArrowRight size={12} className="shrink-0 opacity-40" />
          </button>
        ))}
      </div>

      {/* Right: parent link + children grid */}
      <div className="flex-1 px-2 py-0.5">

        {/* Parent pillar link */}
        <NavigationMenuLink asChild>
          <Link
            href={active.href}
            className="group flex items-center justify-between gap-2 px-3 py-2.5 mb-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-100"
          >
            <span className="text-sm font-semibold text-secondary group-hover:text-secondary transition-colors">
              {active.label}
            </span>
            <ArrowUpRight size={13} className="text-secondary/50 group-hover:text-secondary transition-transform duration-200 ease-out group-hover:translate-x-1.5 group-hover:-translate-y-0.5 shrink-0" />
          </Link>
        </NavigationMenuLink>

        <div className="mx-1 mb-1.5 border-t border-slate-100" />

        {/* Children 2-col grid */}
        <div className="grid grid-cols-2 gap-0.5">
          {active.children.map(child => (
            <NavigationMenuLink asChild key={child.href}>
              <Link
                href={child.href}
                className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span className="text-sm font-semibold text-secondary/70 group-hover:text-secondary transition-colors">
                  {child.label}
                </span>
                <ArrowUpRight size={13} className="text-secondary/50 opacity-0 group-hover:opacity-100 group-hover:text-secondary transition-all duration-200 ease-out group-hover:translate-x-1.5 shrink-0" />
              </Link>
            </NavigationMenuLink>
          ))}
        </div>

      </div>
    </div>
  );
}

// ── Simple dropdown ───────────────────────────────────────────────────────────
function SimpleMenu({ items, heading }) {
  return (
    <div className="min-w-40">
      {heading && (
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 px-3">
          {heading}
        </p>
      )}
      {items.map(item => (
        <NavigationMenuLink asChild key={item.href}>
          <Link
            href={item.href}
            className="group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-all"
          >
            <span className="flex items-center gap-2.5">
              <span className="text-sm font-semibold text-secondary group-hover:text-secondary transition-colors">{item.label}</span>
            </span>
            <ArrowUpRight size={13} className="text-secondary/50 opacity-0 group-hover:opacity-100 group-hover:text-secondary transition-all duration-200 ease-out group-hover:translate-x-1.5 shrink-0" />
          </Link>
        </NavigationMenuLink>
      ))}
    </div>
  );
}

// ── Mobile sidebar ────────────────────────────────────────────────────────────

function buildMobileNav({ serviceTabs, servicesDirect, industries, hireDev, tech, resources }) {
  return [
    {
      key: 'services', label: 'Services',
      children: [
        ...serviceTabs.flatMap(tab => [
          { type: 'label', label: tab.label, href: tab.exploreHref },
          ...tab.links.map(l => ({ type: 'link', ...l })),
        ]),
        ...(servicesDirect.length > 0 ? [
          { type: 'label', label: 'Other Services' },
          ...servicesDirect.map(d => ({ type: 'link', ...d })),
        ] : []),
      ],
    },
    {
      key: 'industries', label: 'Industries',
      children: industries.map(i => ({ type: 'link', ...i })),
    },
    {
      key: 'hire', label: 'Hire Developer',
      children: hireDev.map(d => ({ type: 'link', ...d })),
    },
    {
      key: 'tech', label: 'Technologies',
      children: tech.map(d => ({ type: 'link', ...d })),
    },
    { key: 'about', label: 'About Us', href: '/about-us' },
    ...(STORE_VISIBLE ? [{ key: 'store', label: 'Store', href: '/store' }] : []),
    {
      key: 'resources', label: 'Resources',
      children: resources.map(r => ({ type: 'link', ...r })),
    },
  ];
}

function MobileSidebar({ open, onClose, navItems }) {
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const toggle = key => setExpanded(prev => (prev === key ? null : key));

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-[59] transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white z-[60] shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}>

        {/* Sidebar header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100 shrink-0">
          <Link href="/" onClick={onClose}>
            <Image src="/images/velanapps_logo.webp" alt="Velan Software" width={180} height={35} draggable="false" />
          </Link>
          <button
            onClick={onClose}
            className="rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Close menu"
          >
            <X size={20} className="text-slate-600" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto">
          {navItems.map(item => (
            <div key={item.key} className="border-b border-slate-100 last:border-0">
              {item.href ? (
                <Link
                  href={item.href}
                  onClick={onClose}
                  className="flex items-center px-5 py-4 text-sm font-semibold text-slate-800 hover:text-red-600 hover:bg-slate-50 transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => toggle(item.key)}
                    className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
                  >
                    {item.label}
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 transition-transform duration-200 ${expanded === item.key ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <div className={`overflow-hidden transition-all duration-300 ${expanded === item.key ? 'max-h-[70vh]' : 'max-h-0'}`}>
                    <div className="bg-slate-50/80 pb-2 max-h-[45vh] overflow-y-auto">
                      {item.children.map((child, idx) =>
                        child.type === 'label' ? (
                          child.href ? (
                            <Link
                              key={idx}
                              href={child.href}
                              onClick={onClose}
                              className="block px-5 pt-4 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-red-600 transition-colors"
                            >
                              {child.label}
                            </Link>
                          ) : (
                            <p
                              key={idx}
                              className="px-5 pt-4 pb-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest"
                            >
                              {child.label}
                            </p>
                          )
                        ) : (
                          <Link
                            key={child.href}
                            href={child.href}
                            onClick={onClose}
                            className="flex items-center gap-2.5 px-6 py-2.5 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
                            {child.label}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div className="p-5 border-t border-slate-100 shrink-0 space-y-3">
          <a href={`tel:${siteConfig.phoneTel}`} className="flex items-center gap-1 text-center justify-center text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
            <PhoneCall
              size={16}
              className={`text-slate-500 transition-transform duration-200`}
            /> {siteConfig.phone}
          </a>
          <Button asChild className="w-full bg-red-gradient border-0 text-white btn-glass gap-2 group h-10 px-4">
            <Link href="/contact-us" onClick={onClose}>
              <span className="relative z-10 flex items-center gap-2 group-hover:text-secondary transition-colors duration-400">
                Contact Us
                <ArrowUpRight size={14} className="transition-transform duration-200 ease-out group-hover:translate-x-1.5" />
              </span>
            </Link>
          </Button>
        </div>

      </div>
    </>
  );
}

// ── Shared trigger class ──────────────────────────────────────────────────────

function getTriggerCls(isActive) {
  const base = 'text-sm font-semibold bg-transparent hover:bg-transparent focus:bg-transparent data-active:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-secondary data-[state=open]:font-semibold transition-all px-3 py-2';
  return isActive
    ? `${base} text-primry/40 underline underline-offset-[6px] decoration-2 decoration-primary/40`
    : `${base} text-secondary/90 hover:text-secondary`;
}

// ── Header ────────────────────────────────────────────────────────────────────

export default function HeaderClient() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const isStorePage = pathname === '/store' || pathname.startsWith('/store/');
  const isStoreAuthPage = pathname === '/store/login' || pathname === '/store/register' || pathname === '/store/forgot-password' || pathname === '/store/set-password';

  const livePaths = useLivePaths();
  const liveServiceTabs = useMemo(
    () => filterLiveGroups(SERVICES_TABS, livePaths, { hrefKey: 'exploreHref', childHrefKey: 'href', childrenKey: 'links', requireParentLive: true }),
    [livePaths]
  );
  const liveServicesDirect = useMemo(() => filterLiveLinks(SERVICES_DIRECT, livePaths), [livePaths]);
  const allServiceTabs = useMemo(() => buildAllServiceTabs(liveServiceTabs), [liveServiceTabs]);
  const liveIndustries = useMemo(() => filterLiveLinks(INDUSTRIES, livePaths), [livePaths]);
  const liveHireDev = useMemo(() => filterLiveGroups(HIRE_DEV, livePaths), [livePaths]);
  const liveTech = useMemo(() => filterLiveGroups(TECH, livePaths), [livePaths]);
  const liveResources = useMemo(() => filterLiveLinks(RESOURCES, livePaths), [livePaths]);
  const mobileNavItems = useMemo(
    () => buildMobileNav({
      serviceTabs: liveServiceTabs,
      servicesDirect: liveServicesDirect,
      industries: liveIndustries,
      hireDev: liveHireDev,
      tech: liveTech,
      resources: liveResources,
    }),
    [liveServiceTabs, liveServicesDirect, liveIndustries, liveHireDev, liveTech, liveResources]
  );

  const activeMenu = getActiveMenu(pathname, {
    allServiceTabs,
    industries: liveIndustries,
    hireDev: liveHireDev,
    tech: liveTech,
    resources: liveResources,
  });

  return (
    <>
      {!isStoreAuthPage && (
      <header className={`max-w-[1440px] mx-auto fixed inset-x-4 z-50 rounded-xl bg-white/95 backdrop-blur-lg border border-slate-200/60 shadow-sm ${isStorePage ? 'top-14' : 'top-3'}`}>
        <div className="px-5 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4 md:gap-8">

          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 shrink-0">
            <Image
              src="/images/velanapps_logo.webp"
              alt="Velan Software"
              width={180}
              height={35}
              priority
              draggable="false"
            />
          </Link>

          {/* Desktop nav */}
          <NavigationMenu viewport={false} className="hidden xl:flex xl:flex-none xl:w-max 2xl:flex-1 max-w-none! static! [&>div]:static!">
            <NavigationMenuList className="justify-start gap-1">

              <NavigationMenuItem className="static">
                <NavigationMenuTrigger className={getTriggerCls(activeMenu === 'services')}>Services</NavigationMenuTrigger>
                <NavigationMenuContent className="p-0! rounded-xl! shadow-xl left-0! right-0! w-auto!">
                  <ServicesMegaMenu pathname={pathname} tabs={allServiceTabs} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={getTriggerCls(activeMenu === 'industries')}>Industries</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <IndustriesMenu items={liveIndustries} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={getTriggerCls(activeMenu === 'hire')}>Hire Developer</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <HireDevMenu groups={liveHireDev} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={getTriggerCls(activeMenu === 'tech')}>Technologies</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <TechMenu groups={liveTech} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    href="/about-us"
                    className={`px-3 py-2 text-sm font-semibold transition-all ${activeMenu === 'about' ? 'text-secondary underline underline-offset-[6px] decoration-2 decoration-primary/40' : 'text-secondary/90 hover:text-secondary'}`}
                  >
                    About Us
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={getTriggerCls(activeMenu === 'resources')}>Resources</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <SimpleMenu items={liveResources} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {STORE_VISIBLE && (
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      href="/store"
                      className={`px-3 py-2 text-sm flex flex-wrap items-center justify-center gap-1.5 font-semibold transition-all ${activeMenu === 'store' ? 'text-secondary underline underline-offset-[6px] decoration-2 decoration-primary/40' : 'text-secondary/90 hover:text-secondary'}`}
                    >
                      <ShoppingCart className="relative -top-[2px]" size={14} /> Store
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              )}

            </NavigationMenuList>
          </NavigationMenu>

          {/* Right side */}
          <div className="flex items-center gap-4 shrink-0">
            <a
              href={`tel:${siteConfig.phoneTel}`}
              className="group hidden xl:inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors xl:max-[1300px]:relative xl:max-[1300px]:h-10 xl:max-[1300px]:w-10 xl:max-[1300px]:justify-center xl:max-[1300px]:rounded-full xl:max-[1300px]:bg-primary xl:max-[1300px]:shadow-glow-sm xl:max-[1300px]:hover:bg-primary/90 xl:max-[1300px]:hover:shadow-glow-red xl:max-[1300px]:hover:scale-110 xl:max-[1300px]:active:scale-95 xl:max-[1300px]:transition-all xl:max-[1300px]:duration-300 xl:max-[1300px]:cursor-pointer"
            >
              <span className="hidden xl:max-[1300px]:block absolute inset-0 rounded-full bg-primary/70 animate-ping pointer-events-none" />
              <PhoneCall
                size={16}
                className="relative text-slate-700 origin-center group-hover:animate-[phone-shake_0.4s_ease-in-out] xl:max-[1300px]:text-white"
              />
              <span suppressHydrationWarning className="relative xl:max-[1300px]:hidden">{siteConfig.phone}</span>
            </a>
            <Button asChild size="sm" className="hidden xl:inline-flex bg-red-gradient border-0 btn-glass group h-10 px-4">
              <Link href="/contact-us">
                <span className="relative z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
                  Contact Us
                  <ArrowUpRight size={14} className="transition-transform duration-200 ease-out group-hover:translate-x-1.5" />
                </span>
              </Link>
            </Button>

            {/* Persistent mobile call button — visible without opening the menu */}
            <a
              href={`tel:${siteConfig.phoneTel}`}
              className="xl:hidden rounded-lg hover:bg-primary/10 transition-colors"
              aria-label={`Call ${siteConfig.phone}`}
            >
              <PhoneCall size={20} className="text-primary" />
            </a>

            {/* Persistent mobile contact button — visible without opening the menu */}
            <Link
              href="/contact-us"
              className="xl:hidden rounded-lg hover:bg-primary/10 transition-colors"
              aria-label="Contact us"
            >
              <MessageSquare size={20} className="text-primary" />
            </Link>

            {/* Hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="xl:hidden rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Open menu"
            >
              <Menu size={22} className="text-slate-700" />
            </button>
          </div>

        </div>
      </header>
      )}

      {!isStoreAuthPage && <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} navItems={mobileNavItems} />}
    </>
  );
}